import { useState, useEffect, useCallback, useRef } from 'react';

const AUTO_BUILD_KEYS = [
  'totalAmountCents', 'installmentFrequency',
  'installmentsCount', 'firstInstallmentDueOn',
];

const DEBOUNCE_MS = 300;

export const EDITABLE_STATUSES = ['scheduled', 'overdue', 'failed'];

export const isEditableInstallment = (row) => !row.derived_status || EDITABLE_STATUSES.includes(row.derived_status);

// Merge persisted IDs from the initial schedule into the freshly previewed one
// by `sequence`. Returns `{ visible, deletions }`:
//   - visible:   the installments to render and edit (no tombstones)
//   - deletions: `{ id, _destroy: true }` rows the form must include on submit
//                for sequences that dropped out of the new schedule
// Without this, count changes in edit mode re-create installments and orphan
// any per-installment state attached to the old IDs.
//
// Exported for unit testing — the hook itself uses it internally.
export function reconcileWithInitial(next, initialInstallments) {
  if (!initialInstallments || initialInstallments.length === 0) {
    return { visible: next, deletions: [] };
  }
  const idBySeq = Object.fromEntries(initialInstallments.map((i) => [i.sequence, i.id]));
  const nextSeqs = new Set(next.map((n) => n.sequence));
  const visible = next.map((n) => (idBySeq[n.sequence] ? { ...n, id: idBySeq[n.sequence] } : n));
  const deletions = initialInstallments
    .filter((i) => !nextSeqs.has(i.sequence))
    .map((i) => ({ id: i.id, _destroy: true }));
  return { visible, deletions };
}

function canAutoBuild(form) {
  return form.totalAmountCents > 0
      && form.installmentsCount > 0
      && form.installmentFrequency
      && form.firstInstallmentDueOn;
}

function formStateFromPledge(p, initialValues = {}) {
  if (!p) {
    const todayIso = new Date().toISOString().slice(0, 10); // 'YYYY-MM-DD'
    return {
      name: '', fundId: null, givingCampaignId: null, appealId: null, solicitorId: null,
      totalAmountCents: 0, pledgeDate: todayIso, expectedFulfillmentDate: '',
      installmentFrequency: 'monthly', installmentsCount: 12, firstInstallmentDueOn: todayIso,
      notes: '', termsAndConditions: '', lockVersion: 0, coverProcessing: false,
      ...initialValues,
    };
  }
  return {
    name: p.name,
    fundId: p.fund?.id,
    givingCampaignId: p.giving_campaign?.id,
    appealId: p.appeal?.id,
    solicitorId: p.solicitor?.id,
    totalAmountCents: p.total_amount_cents,
    pledgeDate: p.pledge_date,
    expectedFulfillmentDate: p.expected_fulfillment_date,
    installmentFrequency: p.installment_frequency,
    installmentsCount: p.installments_count,
    firstInstallmentDueOn: p.first_installment_due_on || '',
    notes: p.notes || '',
    termsAndConditions: p.terms_and_conditions || '',
    lockVersion: p.lock_version,
    coverProcessing: !!p.cover_processing,
  };
}

export default function usePledgeScheduleBuilder({ initialPledge, initialValues, mode, previewSchedule, confirmReplace }) {
  const [form, setForm]               = useState(() => formStateFromPledge(initialPledge, initialValues));
  const [installments, setInstallments] = useState(initialPledge?.installments ?? []);
  // Persisted installments dropped by a preview rebuild; must be sent back as
  // `{ id, _destroy: true }` rows in installments_attributes on submit.
  const [pendingDeletions, setPendingDeletions] = useState([]);
  const [scheduleDirty, setScheduleDirty] = useState(false);
  const [shapeChanged, setShapeChanged] = useState(mode !== 'edit');
  // false in create (auto-populate freely); true in edit (preserve persisted value)
  const [expectedFulfillmentTouched, setExpectedFulfillmentTouched] = useState(!!initialPledge);
  // Frozen at mount so we always have the persisted IDs/sequences to merge against,
  // even after the local `installments` state has been replaced by a preview response.
  const initialInstallmentsRef = useRef(initialPledge?.installments || []);
  const scheduleLocked = mode === 'edit' && initialInstallmentsRef.current.some((row) => !isEditableInstallment(row));
  const scheduleCanceled = mode === 'edit' && !!initialPledge?.schedule_canceled_at;

  useEffect(() => {
    if (!canAutoBuild(form) || scheduleDirty || !shapeChanged) return;
    const handle = setTimeout(async () => {
      const next = await previewSchedule({
        total_amount_cents:        form.totalAmountCents,
        installment_frequency:     form.installmentFrequency,
        installments_count:        form.installmentsCount,
        first_installment_due_on:  form.firstInstallmentDueOn,
      });
      // In edit mode, re-attach persisted IDs by sequence so installments_attributes
      // updates in place; sequences that dropped out get `_destroy: true` tombstones
      // (held separately from the displayed rows).
      if (mode === 'edit') {
        const { visible, deletions } = reconcileWithInitial(next, initialInstallmentsRef.current);
        setInstallments(visible);
        setPendingDeletions(deletions);
      } else {
        setInstallments(next);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [form.totalAmountCents, form.installmentFrequency,
      form.installmentsCount, form.firstInstallmentDueOn,
      scheduleDirty, shapeChanged, previewSchedule, mode]);

  // Auto-derive expectedFulfillmentDate from the last installment's due_date returned by
  // preview_schedule. Source of truth stays in Ruby (Pledge::STEP_MONTHS); no local date math.
  // Skipped when the user has manually edited the field (expectedFulfillmentTouched = true).
  useEffect(() => {
    if (expectedFulfillmentTouched) return;
    if (installments.length === 0) return;
    const last = installments[installments.length - 1];
    if (!last?.due_date) return;
    if (last.due_date === form.expectedFulfillmentDate) return;
    setForm((f) => ({ ...f, expectedFulfillmentDate: last.due_date }));
  }, [installments, expectedFulfillmentTouched, form.expectedFulfillmentDate]);

  const editInstallment = useCallback((idx, patch) => {
    setInstallments((prev) =>
      prev.map((row, i) => (i === idx ? { ...row, ...patch } : row))
    );
    setScheduleDirty(true);
  }, []);

  const requestFormChange = useCallback(async (key, value) => {
    if (mode === 'edit' && AUTO_BUILD_KEYS.includes(key) && !scheduleLocked && !scheduleCanceled) {
      if (scheduleDirty) {
        const ok = await confirmReplace();
        if (!ok) return;
        setScheduleDirty(false);
      }
      setShapeChanged(true);
    }
    if (key === 'expectedFulfillmentDate') {
      setExpectedFulfillmentTouched(true);
    }
    setForm((f) => ({ ...f, [key]: value }));
  }, [mode, scheduleDirty, scheduleLocked, scheduleCanceled, confirmReplace]);

  const scheduleSum = installments.reduce((s, r) => s + Number(r.amount_cents || 0), 0);
  const scheduleMatches = scheduleCanceled || scheduleSum === Number(form.totalAmountCents);

  return {
    form, installments, pendingDeletions, scheduleSum, scheduleMatches, scheduleLocked,
    editInstallment, requestFormChange,
  };
}
